import { Button, Form, Input, message, Select} from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { titleAutoGeneratorService } from '../service/titleAutoGeneratorService';
import {
  AutomationTypeOptions,
  CreateTitleAutoGeneratorRequest,
  TitleAutoGenerator,
} from '../type/titleAutoGeneratorType';

const TitleAutoGeneratorForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<TitleAutoGenerator | null>(null);
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await titleAutoGeneratorService.fetchById(Number(id));
      setInitialData(data);
      form.setFieldsValue({
        slug: data.slug,
        automation_type: data.automation_type,
        note: data.note,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (isEditMode && id) {
        // Edit mode
        await titleAutoGeneratorService.update(Number(id), {
          slug: values.slug,
          automation_type: values.automation_type,
          note: values.note || null,
        });
        message.success('Updated successfully.');
        // Refresh page to reflect latest data
        await loadData();
      } else {
        // Create mode
        const requestData: CreateTitleAutoGeneratorRequest = {
          slug: values.slug,
          automation_type: values.automation_type,
          note: values.note || null,
        };
        const result = await titleAutoGeneratorService.create(requestData);
        message.success('Created successfully.');
        navigate(`/title-auto-generator/edit/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to submit:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Component.BasePageComponent>
      <div>
        <div className="season-details-page">
          <h1>{isEditMode ? 'Edit Title Auto Generator' : 'Add Title Auto Generator'}</h1>
          <Form form={form} layout="vertical" className="season-details-form">
            <Form.Item
              name="slug"
              label="Season Slug"
              rules={[
                { required: true, message: 'Please enter Season slug.' },
                { max: 200, message: 'Season slug must be 200 characters or less.' },
                {
                  pattern: /^[a-zA-Z0-9-_]+$/,
                  message: 'Season slug can only contain letters, numbers, hyphens, and underscores.',
                },
              ]}
              help={
                'Enter the unique slug for the Season.'
                // isEditMode
                //   ? '⚠️ Season slug cannot be modified.'
                //   : 'Enter the unique slug for the Season.'
              }
            >
              <Input
                placeholder="e.g. morning-news"
                // disabled={isEditMode}
                // style={isEditMode ? { backgroundColor: '#f5f5f5' } : {}}
              />
            </Form.Item>

            <Form.Item
              name="automation_type"
              label="Automation Type"
              rules={[{ required: true, message: 'Please select an Automation Type.' }]}
              help="Select the title auto-generation method."
            >
              <Select placeholder="Select an Automation Type" options={AutomationTypeOptions} />
            </Form.Item>

            <Form.Item
              name="note"
              label="Note (Optional)"
              rules={[{ max: 200, message: 'Note must be 200 characters or less.' }]}
              help="Enter additional notes. (Max 200 characters)"
            >
              <Input.TextArea
                placeholder="e.g. Morning News Program"
                rows={4}
                maxLength={200}
                showCount
              />
            </Form.Item>

            {isEditMode && initialData && (
              <>
                <Form.Item label="Created">
                  <Input
                    value={new Date(initialData.created).toLocaleString('en-US')}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>

                <Form.Item label="Modified">
                  <Input
                    value={new Date(initialData.modified).toLocaleString('en-US')}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </div>

        <div className="season-detail-update-button">
          <Button onClick={() => navigate('/title-auto-generator')} style={{ marginRight: '10px' }}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(TitleAutoGeneratorForm);
